import React, { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { onClickOutside } from "@/hooks/click-outside";

export type DropdownItem = {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  icon?: React.ReactNode;
};

type DropdownProps = {
  trigger: React.ReactNode;
  items: Array<DropdownItem>;
  header?: React.ReactNode;
  align?: "left" | "right";
  className?: string;
};

function Dropdown({
  trigger,
  items,
  header,
  align = "right",
  className,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  onClickOutside(wrapperRef, () => setOpen(false));

  return (
    <div ref={wrapperRef} className={twMerge("relative", className)}>
      <div onClick={() => setOpen(!open)}>{trigger}</div>
      {open && (
        <div
          className={twMerge(
            "absolute top-full mt-2 z-30 min-w-[200px] rounded-xl border border-line-subtle bg-surface-card shadow-lg overflow-hidden",
            align === "right" ? "right-0" : "left-0"
          )}
        >
          {header && (
            <div className="px-4 py-3 border-b border-line-subtle">{header}</div>
          )}
          <ul className="py-1">
            {items.map((item) => (
              <li key={item.label}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    item.onClick();
                  }}
                  className={twMerge(
                    "w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors hover:bg-surface-inset",
                    item.destructive ? "text-red-500" : "text-ink-secondary"
                  )}
                >
                  {item.icon}
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default Dropdown;
